import React, { useRef, useState, useEffect } from 'react'
import { apiClient } from '@/services/api'
import BlocklyWorkspace, { type BlocklyWorkspaceHandle } from '@/components/blockly/BlocklyWorkspace'
import * as Blockly from 'blockly'
import { Copy, Check, Loader2 } from 'lucide-react'

interface BlocklyQuestion {
  id: number | string
  title: string
  difficulty: string
  question: string
  allowedCategories: string[]
  correct_xml?: string
  correct_code?: string
}

export default function BlocklyXmlGenerator() {
  const [questions, setQuestions] = useState<BlocklyQuestion[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<number | string | null>(null)
  const [exportedXml, setExportedXml] = useState<string>('')
  const [copied, setCopied] = useState(false)
  const workspaceRef = useRef<BlocklyWorkspaceHandle>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await apiClient.getQuestionBankQuestions('coding')
        const items = (res.data || []).map((q: any) => ({
          id: q.id,
          title: q.title || q.data?.title || `Question ${q.id}`,
          difficulty: q.difficulty || 'medium',
          question: q.data?.question || '',
          allowedCategories: q.data?.allowedCategories || ['Logic', 'Loops', 'Math', 'Text', 'Variables', 'Functions'],
          correct_xml: q.data?.correct_xml,
          correct_code: q.data?.correct_code,
        }))
        setQuestions(items)
        if (items.length > 0) setSelectedId(items[0].id)
      } catch {
        // failed to load
      } finally {
        setLoading(false)
      }
    }
    fetchQuestions()
  }, [])

  const selectedQuestion = questions.find(q => q.id === selectedId)

  const handleExportXml = () => {
    const container = containerRef.current
    if (!container) return
    const workspaceDiv = container.querySelector('.injectionDiv')
    if (!workspaceDiv) return
    const workspaceId = workspaceDiv.id
    const ws = Blockly.common.getWorkspaceById(workspaceId) as Blockly.WorkspaceSvg
    if (!ws) return
    const dom = Blockly.Xml.workspaceToDom(ws)
    const xmlText = Blockly.Xml.domToText(dom)
    setExportedXml(xmlText)
    setCopied(false)
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(exportedXml)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-theme-muted" />
      </div>
    )
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text p-8">
        <h1 className="text-2xl font-bold mb-6">Blockly XML Generator (Dev Tool)</h1>
        <p className="text-theme-muted">No coding questions found in the question bank.</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-theme-bg text-theme-text p-8">
      <h1 className="text-2xl font-bold mb-6">Blockly XML Generator (Dev Tool)</h1>

      <div className="mb-4 flex items-center gap-4">
        <select
          value={String(selectedId)}
          onChange={e => setSelectedId(e.target.value)}
          className="border border-[var(--color-border)] rounded-lg px-3 py-2 bg-[var(--color-surface)] text-theme-text"
        >
          {questions.map(q => (
            <option key={q.id} value={q.id}>
              #{q.id} — {q.title} ({q.difficulty})
            </option>
          ))}
        </select>

        <button
          onClick={handleExportXml}
          className="px-4 py-2 rounded-lg font-medium bg-theme-primary text-theme-on-primary"
        >
          Export XML
        </button>
      </div>

      {selectedQuestion && (
        <>
          <div className="mb-4 p-4 bg-[var(--color-surface-deep)] rounded-lg border border-[var(--color-border)]">
            <p className="text-sm font-semibold mb-1">Question:</p>
            <p className="text-sm whitespace-pre-wrap">{selectedQuestion.question}</p>
            <p className="text-xs text-theme-muted mt-2">
              Categories: {selectedQuestion.allowedCategories.join(', ')}
            </p>
            {selectedQuestion.correct_xml && (
              <p className="text-xs text-green-500 mt-1">Has existing correct_xml</p>
            )}
          </div>

          <div ref={containerRef}>
            <BlocklyWorkspace
              key={String(selectedId)}
              ref={workspaceRef}
              allowedCategories={selectedQuestion.allowedCategories}
              initialXml={selectedQuestion.correct_xml}
            />
          </div>
        </>
      )}

      {exportedXml && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold">Exported XML:</span>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-[var(--color-surface-deep)] border border-[var(--color-border)] hover:bg-[var(--color-surface)]"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <pre className="p-4 bg-[var(--color-surface-deep)] rounded-lg border border-[var(--color-border)] text-xs overflow-auto max-h-64 whitespace-pre-wrap">
            {exportedXml}
          </pre>
        </div>
      )}
    </div>
  )
}
